import React, { useState, useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import { sendMessage, sendInternalMessage, sendDirectMessage, markMessageAsRead } from '../../services/messages';
import { sendSupportTicketReply } from '../../services/supportTickets';

const MessageThread = ({ conversation, onMessageSent, activeTab = 'applicants', threadLoading = false }) => {
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const threadRef = useRef(null);
  const onMessageSentRef = useRef(onMessageSent);
  onMessageSentRef.current = onMessageSent;

  const messages = conversation?.messages || [];
  
  // Determine if this is an internal staff conversation
  // Priority: activeTab > conversation.isInternal flag
  // If we're in the Staff tab, it's always an internal message
  const isInternal = activeTab === 'staff' || conversation?.isInternal === true;
  
  // Get recipient info: for internal messages, use staffMemberId; for customer messages, use customerId
  let recipientId;
  if (isInternal || activeTab === 'staff') {
    // For internal messages, determine recipient from conversation or messages
    recipientId = conversation?.staffMemberId;
    
    // If not set, try to get it from messages
    if (!recipientId && messages.length > 0) {
      // Find the other staff member from messages
      const firstInternalMsg = messages.find(m => m.isInternal);
      if (firstInternalMsg) {
        // Get the other staff member (not the current user)
        // Since backend filters to show messages where current user is involved,
        // we need to determine which one is the "other"
        const fromUserId = firstInternalMsg.fromUserId?._id || firstInternalMsg.fromUserId?.toString() || firstInternalMsg.fromUserId;
        const toUserId = firstInternalMsg.toUserId?._id || firstInternalMsg.toUserId?.toString() || firstInternalMsg.toUserId;
        
        // For now, use toUserId as recipient (assuming current user is sender)
        // Or check reviewer/approver from licence
        const licenceInfo = firstInternalMsg.licenceId;
        if (licenceInfo) {
          const reviewerId = licenceInfo.reviewerId?._id || licenceInfo.reviewerId?.toString() || licenceInfo.reviewerId;
          const approverId = licenceInfo.approverId?._id || licenceInfo.approverId?.toString() || licenceInfo.approverId;
          
          // Use the one that's not null and matches one of the user IDs in the message
          if (reviewerId && (reviewerId.toString() === fromUserId?.toString() || reviewerId.toString() === toUserId?.toString())) {
            recipientId = reviewerId.toString() === fromUserId?.toString() ? toUserId : fromUserId;
          } else if (approverId && (approverId.toString() === fromUserId?.toString() || approverId.toString() === toUserId?.toString())) {
            recipientId = approverId.toString() === fromUserId?.toString() ? toUserId : fromUserId;
          } else {
            recipientId = toUserId || fromUserId;
          }
        } else {
          recipientId = toUserId || fromUserId;
        }
      }
    }
    
    // If still no recipientId and we're in staff tab, we need to get it from the licence
    // For SuperAdmin, we should be able to send to reviewer or approver
    // For now, if recipientId is still null, we'll need to fetch licence details
    // But for immediate fix, we'll allow sending without toUserId (visible to all staff)
    if (!recipientId && activeTab === 'staff') {
      console.warn('No recipientId found for staff message. Message will be visible to all staff.');
    }
  } else {
    recipientId = conversation?.customerId || 
                  conversation?.messages?.find(m => m.fromCustomerId)?._id || 
                  conversation?.messages?.find(m => m.fromCustomerId);
  }

  useEffect(() => {
    scrollToBottom();
    // Mark messages as read when thread is viewed
    if ((conversation?.licenceId || conversation?.isDirectMessage) && messages.length > 0) {
      // Mark all unread messages in this conversation as read
      const unreadMessages = messages.filter(msg => {
        // Check if message is unread based on isUnreadForCurrentUser flag or read status
        return msg.isUnreadForCurrentUser !== undefined 
          ? msg.isUnreadForCurrentUser 
          : !msg.read;
      });
      
      if (unreadMessages.length === 0) return;
      
      // Collect promises for all mark-as-read calls
      const readPromises = [];
      
      unreadMessages.forEach(msg => {
        const msgId = msg._id || msg.id;
        if (!msgId) return;
        
        // Mark as read if:
        // 1. Message is to current admin (toUserId exists) - for both internal and customer messages
        // 2. OR message is from customer (fromCustomerId exists) - visible to all admins
        // 3. OR message is internal with toUserId: null (visible to all staff)
        const toUserId = msg.toUserId?._id || msg.toUserId?.toString() || msg.toUserId;
        const shouldMarkAsRead = 
          (toUserId) || // Message to current admin
          (msg.fromCustomerId && !msg.isInternal) || // Customer message visible to all admins
          (msg.isInternal && !toUserId); // Internal message visible to all staff (toUserId is null)
        
        if (shouldMarkAsRead) {
          readPromises.push(
            markMessageAsRead(msgId).catch(err => console.error('Failed to mark as read:', err))
          );
        }
      });
      
      // Only call onMessageSent once after all read marks are done
      if (readPromises.length > 0) {
        Promise.all(readPromises).then(() => {
          if (onMessageSentRef.current) {
            onMessageSentRef.current(); // Trigger reload to update unread counts
          }
        });
      }
    }
  }, [conversation?.licenceId, messages.length, conversation?.directCustomerId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!messageText.trim()) return;

    if (conversation?.ticketId) {
      setSending(true);
      try {
        await sendSupportTicketReply(conversation.ticketId, { body: messageText.trim() });
        setMessageText('');
        if (onMessageSentRef.current) onMessageSentRef.current();
      } catch (error) {
        console.error('Failed to send support reply:', error);
        alert('Failed to send reply. Please try again.');
      } finally {
        setSending(false);
      }
      return;
    }

    if (conversation?.isDirectMessage && conversation?.directCustomerId) {
      setSending(true);
      try {
        await sendDirectMessage(conversation.directCustomerId, {
          body: messageText.trim()
        });
        setMessageText('');
        if (onMessageSentRef.current) onMessageSentRef.current();
      } catch (error) {
        console.error('Failed to send direct message:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!conversation?.licenceId) return;

    const shouldSendInternal = activeTab === 'staff';
    setSending(true);
    try {
      if (shouldSendInternal) {
        await sendInternalMessage(conversation.licenceId, {
          body: messageText.trim(),
          toUserId: recipientId || undefined
        });
      } else {
        await sendMessage(conversation.licenceId, {
          body: messageText.trim(),
          toCustomerId: recipientId || undefined,
          isInternal: false
        });
      }
      setMessageText('');
      if (onMessageSentRef.current) onMessageSentRef.current();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    // if (e.key === 'Enter' && !e.shiftKey) {
    //   e.preventDefault();
    //   // handleSend();
    // }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Under Review':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'Re-review':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'Quote Pending':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'Approved':
        return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center text-gray-500">
          <p className="text-sm">Select a conversation to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#EBEBEB] flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">
            {conversation.licenceName || 'Unknown Licence'}
          </h2>
          {conversation.licenceStatus && (
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(conversation.licenceStatus)}`}>
              {conversation.licenceStatus}
            </span>
          )}
        </div>
        {(conversation.customerName || conversation.staffMemberName) && (
          <p className="text-sm text-gray-600">
            {conversation.isInternal ? conversation.staffMemberName : conversation.customerName}
            {(conversation.customerEmail || (conversation.isInternal && conversation.staffMemberId)) && 
              ` • ${conversation.isInternal ? 'Staff' : conversation.customerEmail}`}
          </p>
        )}
      </div>

      {/* Messages */}
      <div ref={threadRef} className="flex-1 overflow-y-auto p-6 min-h-0">
        {/* Loading indicator */}
        {threadLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        {/* Timeline entry for licence request (hide for support tickets) */}
        {!threadLoading && !conversation.ticketId && !conversation.isDirectMessage && conversation.licenceRefNumber && (
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#10B981" strokeWidth="1.5">
              <path d="M13.3333 4L6 11.3333L2.66667 8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>
              {conversation.customerName || 'Customer'} requested a licence
            </span>
            <span className="text-xs">
              {conversation.lastMessage?.timestamp 
                ? new Date(conversation.lastMessage.timestamp).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })
                : ''}
            </span>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble
            key={message._id || message.id}
            message={message}
            isUser={conversation.ticketId ? !!message.fromCustomer : !!message.fromCustomerId}
            licenceId={conversation.licenceId}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="px-6 py-4 border-t border-[#EBEBEB] flex-shrink-0 bg-white">
        <div className="relative flex items-center">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            disabled={sending}
            className="w-full h-11 pl-4 pr-14 bg-[#F8F9FA] rounded-lg text-[14px] text-[#212529] placeholder-[#73827C] focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 border-0"
          />
          <button
            onClick={handleSend}
            disabled={sending || !messageText.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-[#40C191] hover:bg-[#EBF9F3] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-[#40C191] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M18.3333 1.66667L9.16667 10.8333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.3333 1.66667L12.5 18.3333L9.16667 10.8333L1.66667 7.5L18.3333 1.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;
